import React from 'react';
import { RevenueBar } from './charts/RevenueBar';
import { AgingPie } from './charts/AgingPie';
import { KpiCard } from './kpi/KpiCard';
import { useDashboardSummary } from '../../hooks/useDashboardSummary';
import { FiTrendingUp, FiBookOpen, FiClock, FiCreditCard } from 'react-icons/fi';
import { IconType } from 'react-icons';

function formatCurrency(value: number) {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

type Accent = 'brand' | 'indigo' | 'red' | 'amber' | 'green' | 'yellow';

const kpis: Array<{
  key: string;
  label: string;
  value: string;
  icon: IconType;
  accent: Accent;
}> = [
  { key:'revenue',  label:'MTD Revenue',  value:formatCurrency(0),  icon: FiTrendingUp, accent:'brand'  },
  { key:'balance',  label:'Open Balance', value:formatCurrency(0),  icon: FiBookOpen,  accent:'indigo' },
  { key:'late',     label:'Late Fees',    value:formatCurrency(0),  icon: FiClock,     accent:'red'    },
  { key:'expenses', label:'Expenses',     value:formatCurrency(0),  icon: FiCreditCard,accent:'amber'  },
];

export const DashboardPage: React.FC = () => {
  console.log('[DashboardPage] Component rendered');
  
  const { data, error, isLoading } = useDashboardSummary();
  
  console.log('[DashboardPage] useDashboardSummary result:', { data, error, isLoading });
  
  if (isLoading) {
    return (
      <main className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 mb-6">Dashboard</h1>
        <div className="grid gap-5 xl:grid-cols-4 lg:grid-cols-2">
          {kpis.map(k => (
            <div key={k.key} className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 animate-pulse dark:bg-neutral-900 dark:border-neutral-800">
              <div className="h-3 w-24 bg-gray-200 rounded mb-3" />
              <div className="h-7 w-32 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </main>
    );
  }
  
  if (error) {
    return (
      <main className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 mb-6">Dashboard</h1>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">Failed to load dashboard data. Please try again later.</p>
        </div>
      </main>
    );
  }
  
  if (!data) {
    return (
      <main className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 mb-6">Dashboard</h1>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">No dashboard data available.</p>
        </div>
      </main>
    );
  }

  const kpiData = [
    { ...kpis[0], value: formatCurrency(data.mtdRevenue) },
    { ...kpis[1], value: formatCurrency(data.openBalance) },
    { ...kpis[2], value: formatCurrency(data.lateFees) },
    { ...kpis[3], value: formatCurrency(data.expenses) },
  ];

  return (
    <main className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 mb-6">Dashboard</h1>
      <div className="grid gap-5 xl:grid-cols-4 lg:grid-cols-2">
        {kpiData.map(k=>(
          <KpiCard
            key={k.key}
            data-kpi={k.key}
            label={k.label}
            value={k.value}
            icon={k.icon}
            accent={k.accent}
            tabIndex={0}
            role="group"
          />
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-2 mt-8">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 aspect-[16/9] dark:bg-neutral-900 dark:border-neutral-800">
          <RevenueBar monthly={data.monthlyRevenue} />
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 aspect-[16/9] dark:bg-neutral-900 dark:border-neutral-800">
          <AgingPie buckets={data.agingBuckets} />
        </div>
      </div>
    </main>
  );
}; 